import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UsersAPI } from "@/admin/api";
import { useAdmin } from "@/admin/store";
import { BtnGhost, BtnPrimary, Confirm, EmptyState, Field, Modal, PageHeader, SearchInput, StatusPill, inputCls, selectCls } from "@/components/admin/ui";

const FILTERS = ["All", "Active", "Deleted"];
const empty = { username: "", email: "", password: "", roleId: 5, isDeleted: false };

const fmt = (d) => {
  if (!d) return "—";
  const dateStr = d.includes("T") ? d.split("T")[0] : d;
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const Users = () => {
  const { log } = useAdmin();
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);

  const { data: users = [], isLoading: usersLoading } = useQuery({ queryKey: ["users"], queryFn: UsersAPI.getAll });
  const { data: roles = [], isLoading: rolesLoading } = useQuery({ queryKey: ["roles"], queryFn: UsersAPI.getRoles });

  const createMutation = useMutation({
    mutationFn: UsersAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      log("User added", form.username);
      setEditing(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => UsersAPI.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      log("User updated", form.username);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: UsersAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      setConfirmDel(null);
    },
  });

  const roleName = (id) => roles.find((r) => r.roleId === id)?.roleName || "—";

  const rows = users.filter(
    (u) =>
      (filter === "All" || (filter === "Active" ? !u.isDeleted : u.isDeleted)) &&
      ((u.username || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()))
  );

  const openAdd = () => {
    setForm({ ...empty, roleId: roles[0]?.roleId || 5 });
    setErrors({});
    setEditing("new");
  };

  const openEdit = (u) => {
    setForm({ username: u.username, email: u.email, password: "", roleId: u.roleId, isDeleted: u.isDeleted });
    setErrors({});
    setEditing(u.userId);
  };

  const save = () => {
    const errs = {};
    if (!form.username.trim()) errs.username = "A name is required.";
    if (!form.email.includes("@")) errs.email = "A valid email is required.";
    if (editing === "new" && form.password.length < 4) errs.password = "At least 4 characters.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    
    if (editing === "new") {
      createMutation.mutate({
        username: form.username,
        email: form.email,
        password: form.password,
        roleId: Number(form.roleId)
      });
    } else {
      updateMutation.mutate({
        id: editing,
        payload: {
          username: form.username,
          email: form.email,
          roleId: Number(form.roleId),
          isDeleted: form.isDeleted
        }
      });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const formBody = (readonly) => (
    <div className="space-y-5">
      <Field label="Username" error={errors.username}>
        <input data-testid="user-form-username" className={inputCls} value={form.username} disabled={readonly} onChange={(e) => setForm({ ...form, username: e.target.value })} />
      </Field>
      <Field label="Email" error={errors.email}>
        <input data-testid="user-form-email" className={inputCls} value={form.email} disabled={readonly} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </Field>
      {!readonly && editing === "new" && (
        <Field label="Password" error={errors.password}>
          <input data-testid="user-form-password" type="password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Role">
          <select data-testid="user-form-role" className={selectCls} value={form.roleId} disabled={readonly} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
            {roles.map((r) => (
              <option key={r.roleId} value={r.roleId}>{r.roleName}</option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select data-testid="user-form-status" className={selectCls} value={form.isDeleted ? "deleted" : "active"} disabled={readonly} onChange={(e) => setForm({ ...form, isDeleted: e.target.value === "deleted" })}>
            <option value="active">Active</option>
            <option value="deleted">Deleted / Inactive</option>
          </select>
        </Field>
      </div>
      {!readonly && (
        <div className="flex justify-end gap-3 pt-2">
          <BtnGhost onClick={() => setEditing(null)} disabled={isSaving}>Cancel</BtnGhost>
          <BtnPrimary data-testid="user-form-save" onClick={save} disabled={isSaving}>{isSaving ? "Saving..." : (editing === "new" ? "Add User" : "Save Changes")}</BtnPrimary>
        </div>
      )}
    </div>
  );

  if (usersLoading || rolesLoading) return <div className="p-8 text-cream/50">Loading users...</div>;

  return (
    <div data-testid="admin-users">
      <PageHeader title="USERS" sub="The people who hold keys to the Veedu.">
        <SearchInput value={q} onChange={setQ} placeholder="Search users" />
        <BtnPrimary data-testid="add-user-btn" onClick={openAdd}>+ Add User</BtnPrimary>
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`users-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
            className={`border px-4 py-1.5 font-body text-[10px] tracking-[0.25em] uppercase transition-colors ${filter === f ? "border-brass text-brass" : "border-cream/15 text-cream/50"}`}>
            {f}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState title="NO ONE HERE" line="The Veedu is quiet. Add the first user." actionLabel="+ Add User" onAction={openAdd} />
      ) : (
        <div className="border-t border-cream/10">
          <div className="hidden grid-cols-12 gap-4 border-b border-cream/10 py-3 font-body text-[9px] tracking-[0.3em] uppercase text-cream/40 md:grid">
            <span className="col-span-3">Username</span>
            <span className="col-span-3">Email</span>
            <span className="col-span-2">Role</span>
            <span className="col-span-1">Created On</span>
            <span className="col-span-1">Status</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {rows.map((u) => (
            <div key={u.userId} data-testid={`user-row-${u.username}`} className="grid grid-cols-2 gap-3 border-b border-cream/10 py-4 md:grid-cols-12 md:items-center md:gap-4">
              <div className="col-span-2 flex items-center gap-3 md:col-span-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-brass/40 font-display text-sm italic text-brass">
                  {u.username?.slice(0, 1).toUpperCase()}
                </span>
                <span className="font-body text-sm text-cream">{u.username}</span>
              </div>
              <span className="font-body text-xs font-light text-cream/60 md:col-span-3">{u.email}</span>
              <span className="font-body text-xs font-light text-cream/60 md:col-span-2">{u.roleName || roleName(u.roleId)}</span>
              <span className="font-body text-xs font-light text-cream/40 md:col-span-1">{fmt(u.createdOn)}</span>
              <span className="md:col-span-1">
                <StatusPill tone={!u.isDeleted ? "leaf" : "terra"}>{!u.isDeleted ? "Active" : "Deleted"}</StatusPill>
              </span>
              <div className="col-span-2 flex gap-3 md:col-span-2 md:justify-end">
                <button data-testid={`user-view-${u.username}`} onClick={() => { setViewing(u); setForm({ username: u.username, email: u.email, roleId: u.roleId, isDeleted: u.isDeleted }); }} className="font-body text-[10px] tracking-[0.2em] uppercase text-cream/50 hover:text-cream">View</button>
                <button data-testid={`user-edit-${u.username}`} onClick={() => openEdit(u)} className="font-body text-[10px] tracking-[0.2em] uppercase text-brass/80 hover:text-brass">Edit</button>
                {!u.isDeleted ? (
                  <button data-testid={`user-delete-${u.username}`} onClick={() => setConfirmDel(u)} className="font-body text-[10px] tracking-[0.2em] uppercase text-terra/80 hover:text-terra">Delete</button>
                ) : (
                  <button data-testid={`user-restore-${u.username}`} onClick={() => { updateMutation.mutate({ id: u.userId, payload: { ...u, isDeleted: false } }); log("User restored", u.username); }} className="font-body text-[10px] tracking-[0.2em] uppercase text-cream/60 hover:text-brass">Restore</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal title={editing === "new" ? "ADD USER" : "EDIT USER"} onClose={() => setEditing(null)}>
          {formBody(false)}
        </Modal>
      )}
      {viewing && (
        <Modal title={`USER — ${viewing.username.toUpperCase()}`} onClose={() => setViewing(null)}>
          {formBody(true)}
        </Modal>
      )}
      {confirmDel && (
        <Confirm
          testId="user-delete-confirm"
          title="REMOVE THIS USER?"
          body={`${confirmDel.username} will lose their keys to the Veedu. You can restore them later.`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            deleteMutation.mutate(confirmDel.userId);
            log("User removed", confirmDel.username);
          }}
        />
      )}
    </div>
  );
};

export default Users;
